import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Check, ShieldCheck, AlertCircle, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { Logo } from '../components';
import { Button } from '../components';
import { pinProblem, PIN_LENGTH } from '../utils/pin';

/**
 * Set PIN Page
 * Public page for managers to set their PIN after receiving an invitation email
 */
const SetPin = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // When the reset was requested from the mobile app, the link carries
  // ?source=app. After a successful reset we show a message asking the user to
  // return to the app and sign in (instead of the dashboard login redirect).
  const isFromApp = searchParams.get('source') === 'app';

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  // Set once an app-originated reset succeeds, so we show the "return to app"
  // screen instead of the PIN form.
  const [appResetDone, setAppResetDone] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      setVerifying(true);
      const response = await fetch(`${API_URL}/admin/auth/verify-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setUser(result.data);
      } else {
        setError(result.message || 'Invalid or expired invitation link');
      }
    } catch (err) {
      console.error('Verify token error:', err);
      setError('Connection error. Please try again.');
    } finally {
      setVerifying(false);
      setLoading(false);
    }
  };

  /**
   * A PIN is six digits. Anything else never makes it into state, so the field
   * can only ever hold something valid — the submit-time check below is then a
   * backstop (paste, autofill) rather than the only guard.
   */
  const onlyDigits = (value) => value.replace(/\D/g, '').slice(0, PIN_LENGTH);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Six digits and not a guessable pattern, matching the mobile app and the
    // API. This is where a PIN is chosen, so the rule belongs here rather than
    // at sign-in.
    const pinIssue = pinProblem(pin);
    if (pinIssue) {
      toast.error(pinIssue);
      return;
    }
    
    if (pin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/admin/auth/set-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, pin })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('PIN set successfully!');
        if (isFromApp) {
          // Reset started from the mobile app — show a message asking the user
          // to return to the app and sign in with their new PIN.
          setAppResetDone(true);
        } else {
          setTimeout(() => navigate('/login'), 2000);
        }
      } else {
        toast.error(result.message || 'Failed to set PIN');
      }
    } catch (err) {
      console.error('Set PIN error:', err);
      toast.error('Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || verifying) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#1b1f26] flex flex-col items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-[#64748B] dark:text-gray-400 font-medium">Verifying invitation...</p>
      </div>
    );
  }

  if (appResetDone) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#1b1f26] flex flex-col items-center justify-center p-4">
        <div className="bg-white dark:bg-[#262b33] rounded-[24px] shadow-xl p-8 max-w-[440px] w-full text-center border border-gray-100 dark:border-gray-800 animate-in fade-in zoom-in-95 duration-300">
          <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-[22px] font-bold text-[#1E293B] dark:text-gray-100 mb-2">Your Password Has Been Reset</h2>
          <p className="text-[#64748B] dark:text-gray-400 text-[15px] leading-relaxed">
            Your security PIN has been updated successfully. Please open the ServPatch app and sign in with your new PIN.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#1b1f26] flex flex-col items-center justify-center p-4">
        <div className="bg-white dark:bg-[#262b33] rounded-[24px] shadow-xl p-8 max-w-[440px] w-full text-center border border-gray-100 dark:border-gray-800 animate-in fade-in zoom-in-95 duration-300">
          <div className="w-16 h-16 bg-rose-50 dark:bg-rose-900/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-rose-600" />
          </div>
          <h2 className="text-[22px] font-bold text-[#1E293B] dark:text-gray-100 mb-2">Invalid Invitation</h2>
          <p className="text-[#64748B] dark:text-gray-400 text-[15px] mb-8 leading-relaxed">
            {error}. This link may have expired or been used already. Please contact your administrator for a new invitation.
          </p>
          <Button
            variant="primary"
            block
            size="lg"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F9FF] dark:bg-[#1b1f26] flex flex-col items-center justify-center px-4 py-12">
      {/* Brand Logo */}
      <Logo className="mb-8 animate-in slide-in-from-top-4 duration-500" />

      <div className="w-full max-w-[460px] animate-in fade-in zoom-in-95 duration-500">
        <div className="bg-white dark:bg-[#262b33] rounded-[28px] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-white dark:border-gray-800 p-8 sm:p-10 relative overflow-hidden">
          {/* Accent decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 dark:bg-blue-900/20 rounded-full -mr-16 -mt-16 opacity-50"></div>
          
          <div className="relative z-10">
            <div className="text-center mb-8">
              <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="text-[24px] font-bold text-[#1E293B] dark:text-gray-100 tracking-tight">Secure Your Account</h2>
              <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-2">
                Hello <strong>{user?.name}</strong>, please set your security PIN to complete your registration.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-[13px] font-semibold text-[#475569] dark:text-gray-400 mb-1.5 ml-1">New Security PIN <span className="font-normal text-[#94A3B8]">(6 digits)</span></label>
                  <p className="text-[12px] text-[#94A3B8] mb-1.5 ml-1">Not all the same digit (111111) or a run of consecutive digits (123456).</p>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#94A3B8] dark:text-gray-500" />
                    <input
                      type={showPin ? 'text' : 'password'}
                      value={pin}
                      onChange={(e) => setPin(onlyDigits(e.target.value))}
                      placeholder="Enter 6 digits"
                      inputMode="numeric"
                      autoComplete="new-password"
                      maxLength={PIN_LENGTH}
                      className="w-full pl-11 pr-12 py-3 bg-[#F8FAFC] dark:bg-[#31373f] dark:text-gray-200 border border-[#E2E8F0] dark:border-gray-700 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPin(!showPin)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] dark:text-gray-500 hover:text-[#64748B] transition-colors"
                    >
                      {showPin ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#475569] dark:text-gray-400 mb-1.5 ml-1">Confirm Security PIN <span className="font-normal text-[#94A3B8]">(6 digits)</span></label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#94A3B8] dark:text-gray-500" />
                    <input
                      type={showPin ? 'text' : 'password'}
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(onlyDigits(e.target.value))}
                      placeholder="Repeat the 6 digits"
                      inputMode="numeric"
                      autoComplete="new-password"
                      maxLength={PIN_LENGTH}
                      className="w-full pl-11 pr-4 py-3 bg-[#F8FAFC] dark:bg-[#31373f] dark:text-gray-200 border border-[#E2E8F0] dark:border-gray-700 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-start gap-3">
                <Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-[12px] text-blue-700 dark:text-blue-300 leading-relaxed font-medium">
                  Your PIN will be used for all future logins. Keep it secure and do not share it with others.
                </p>
              </div>

              <Button
                type="submit"
                variant="primary"
                block
                size="lg"
                loading={saving}
                className="group"
                rightIcon={<Check className="w-5 h-5 group-hover:scale-110 transition-transform" />}
              >
                {saving ? 'Saving PIN...' : 'Set Security PIN'}
              </Button>
            </form>
          </div>
        </div>

        <p className="mt-8 text-center text-[12px] text-[#94A3B8] dark:text-gray-500 font-medium">
          Secure Access • Care Management System • © 2026 ServPatch
        </p>
      </div>
    </div>
  );
};

export default SetPin;
